/**
 * Shared, headless submission of Authoritative Answers panel feedback to the
 * Feedback Collector API (`POST /gateway/api/feedback-collector-api/v2/feedback`).
 *
 * Both surfaces that render the Authoritative Answers panel (SAIN and Chat)
 * submit through this function, each passing its own `entrypointId` and `origin`
 * so the feedback lands in that surface's established feedback pipeline while
 * remaining attributable to the Authoritative Answers panel.
 *
 * This mirrors the request the `@atlaskit/feedback-collector` component makes but
 * without rendering its modal, because the panel already collects the sentiment,
 * reasons and optional comment inline.
 */

import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';

import { type AuthorityFeedbackSubmission } from './AuthoritativeAnswerAttribution';

/** Feedback Collector entrypoint ids per surface (provisioned GUIDs). */
export const FEEDBACK_ENTRYPOINT_ID_SAIN = '82633dea-7fbf-4faa-9a51-a83a3fd181d9';
export const FEEDBACK_ENTRYPOINT_ID_CHAT = '45c9c029-3180-4960-ab0d-db7cdeaf5f54';

/** Origin markers identifying where in the panel the feedback came from. */
export const AUTHORITY_FEEDBACK_ORIGIN_SAIN = 'sain-authoritative-answer-panel';
export const AUTHORITY_FEEDBACK_ORIGIN_CHAT = 'chat-authoritative-answer-panel';

const FEEDBACK_COLLECTOR_PATH = '/gateway/api/feedback-collector-api/v2/feedback';
const FEEDBACK_CONTEXT_CF = 'customfield_10047';
const ENTRYPOINT_UI_VERSION = '1.0.0';
const MAX_SUMMARY_LENGTH_CHARS = 100;

/**
 * Default field ids / values expected by the Feedback Collector API. These match
 * the defaults used by `@atlaskit/feedback-collector`
 * (platform/packages/web-platform/feedback-collector).
 */
const FIELD_IDS = {
	summary: 'summary',
	description: 'description',
	customerName: 'customfield_10045',
	canBeContacted: 'customfield_10043',
	enrollInResearch: 'customfield_10044',
	feedbackContext: FEEDBACK_CONTEXT_CF,
} as const;

const FIELD_VALUES = {
	canBeContactedDecline: [{ id: '10111' }],
	enrollInResearchDecline: [{ id: '10112' }],
} as const;

type FeedbackField = {
	id: string;
	value: string | object | object[];
};

/**
 * Non-UGC debug attributes for correlating the submission with the answer. All
 * values MUST be safe (no PII / user-generated content). Surfaces provide
 * whatever they have available (SAIN provides all three; Chat may provide none).
 */
export type AuthorityFeedbackDebugAttributes = {
	/** The answer's request/trace id (looked up in Arize). */
	traceId?: string;
	/** The search session the answer belonged to. */
	searchSessionId?: string;
	/** The hashed query (safe — the raw query is never sent). */
	queryHash?: string;
};

export type SubmitAuthorityFeedbackParams = {
	submission: AuthorityFeedbackSubmission;
	/** Feedback Collector entrypoint for the calling surface. */
	entrypointId: string;
	/** Origin marker identifying the calling surface. */
	origin: string;
	cloudId?: string;
	debugAttributes?: AuthorityFeedbackDebugAttributes;
};

const base64Encode = (str: string): string => {
	try {
		// Encode to UTF-8 bytes first so `btoa` handles non-Latin1 input.
		const bytes = new TextEncoder().encode(str);
		let binary = '';
		for (const byte of bytes) {
			binary += String.fromCharCode(byte);
		}
		return btoa(binary);
	} catch {
		return str;
	}
};

const truncate = (text: string, length: number = MAX_SUMMARY_LENGTH_CHARS): string =>
	text.length > length ? `${text.slice(0, length - 1)}…` : text;

/**
 * Submit Authoritative Answers panel feedback to the Feedback Collector API.
 * Defaults to anonymous submission (no AAID, cannot be contacted, not enrolled in
 * research) as the panel does not collect consent.
 *
 * Fire-and-forget from the caller's perspective: the panel confirms optimistically,
 * so failures are logged here rather than surfaced to the user.
 */
export const submitAuthorityFeedback = async ({
	submission: { sentiment, reasons, comment },
	entrypointId,
	origin,
	cloudId,
	debugAttributes,
}: SubmitAuthorityFeedbackParams): Promise<{ success: boolean }> => {
	const { traceId, searchSessionId, queryHash } = debugAttributes ?? {};
	const sentimentLabel = sentiment === 'like' ? 'positive' : 'negative';

	const summary = truncate(`Authoritative answer feedback: ${sentimentLabel}`);

	const context = {
		origin,
		sentiment: sentimentLabel,
		reasons,
		...(cloudId ? { cloudId } : {}),
		...(traceId ? { traceId } : {}),
		...(searchSessionId ? { searchSessionId } : {}),
		...(queryHash ? { queryHash } : {}),
	};

	const description = `${comment ?? ''}

\`\`\`
${JSON.stringify(context, null, 2)}
\`\`\`
`;

	const feedbackContextValue = [
		`origin: ${origin}`,
		`sentiment: ${sentimentLabel}`,
		reasons.length ? `reasons: ${reasons.join(', ')}` : undefined,
		traceId ? `traceId: ${traceId}` : undefined,
		searchSessionId ? `searchSessionId: ${searchSessionId}` : undefined,
		queryHash ? `queryHash: ${queryHash}` : undefined,
	]
		.filter(Boolean)
		.join('\n');

	const fields: FeedbackField[] = [
		{ id: FIELD_IDS.summary, value: summary },
		{ id: FIELD_IDS.description, value: description },
		{ id: FIELD_IDS.customerName, value: 'unknown' },
		{ id: FIELD_IDS.canBeContacted, value: FIELD_VALUES.canBeContactedDecline },
		{ id: FIELD_IDS.enrollInResearch, value: FIELD_VALUES.enrollInResearchDecline },
		{ id: FIELD_IDS.feedbackContext, value: feedbackContextValue },
	];

	const body = {
		feedback: {
			entrypointId,
			entrypointUiVersion: ENTRYPOINT_UI_VERSION,
			fields,
		},
	};

	const postData = base64Encode(JSON.stringify(body));

	try {
		const response = await fetch(FEEDBACK_COLLECTOR_PATH, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ data: postData }),
			credentials: 'include',
		});

		if (!response.ok) {
			throw new Error(`Authority feedback submission failed with status ${response.status}`);
		}

		return { success: true };
	} catch (error) {
		logException({ exception: error as Error, name: 'submitAuthorityFeedback' });
		return { success: false };
	}
};
